import { useCallback, useEffect, useRef, useState } from 'react';
import cardEmvImage from '../../assets/card-emv.svg';
import style from './style.module.scss';
import MessageBoxSet from '../message-box-set';
import { useStore } from '../../lib/hooks/useStore';
import { ViewState, PaymentError } from '../../lib/types/paymentCardState';
import Confirm from '../confirm';
import MainMessageBox from '../../features/main-message-box/main-message-box';
import { AppIconType } from '../../lib/typegen/app-icon-type';
import MagtekCardReaderCardData from '../../lib/devices/card-reader/MagtekCardReaderCardData';
import logger from '../../lib/helpers/logger';
import api from '../../lib/api/api';
import { PayWithMagTekDataRequest } from '../../lib/typegen/pay-with-mag-tek-data-request';
import { LegacyPostAcceptedPayment, LegacyPostRejectedPayment } from '../../lib/types/legacyAdminApi';
import passKeyHelper from '../../lib/helpers/passKey';
import { v4 as guid } from 'uuid';
import BalancesKvpList from '../balances-kvp-list';
import { PaymentCardServerProps } from '../../lib/typegen/payment-card-server-props';
import { PaymentType } from '../../lib/typegen/payment-type';
import enumHelper from '../../lib/helpers/enums';
import { PayWithMagTekDataResponse } from '../../lib/typegen/pay-with-mag-tek-data-response';
import MessageBox from '../message-box';

/**
 * Temporary page to mock EMV card payments using MagTek card reader data.
 */
function PaymentCardEmvMock({
	imageAltKey,
	feeLabelKey,
	paymentAmountLabelKey,
	paymentTotalLabelKey,
	infoBoxLabelKey,
	warningBoxLabelKey,
	cardErrorGenericLabelKey,
	processingPaymentLabelKey,
	tryAgainMessageLabelKey,
	tryAgainNoLabelKey,
	tryAgainYesLabelKey,
}: PaymentCardServerProps) {
	const [magtekData, setMagtekData] = useState<MagtekCardReaderCardData | null>(null);
	const [currentState, setCurrentState] = useState<ViewState>(ViewState.PAY);
	const [error, setError] = useState<PaymentError | null>(null);
	const [errorHint, setErrorHint] = useState<string | null>(null);
	const [hasProcessed, setHasProcessed] = useState<boolean>(false);
	const tapCountRef = useRef<number>(0);
	const tapTimerRef = useRef<number | null>(null);

	const {
		accountStore: { cartItems, setPaymentType },
		uiStore: { handleNavigation },
		kioskStore: { kioskId, companyId, paymentReferenceId },
		sessionStore: { legacyId },
		configStore: { getString, cisType, ppType },
	} = useStore();

	useEffect(() => setPaymentType(PaymentType.Card), [setPaymentType]);

	/**
	 * Handles image tap/click events to detect 3 taps within 5 seconds.
	 * Sets mock MagTek card data when the pattern is detected.
	 */
	const handleImageClick = () => {
		tapCountRef.current += 1;

		// Clear existing timer
		if (tapTimerRef.current) {
			clearTimeout(tapTimerRef.current);
		}

		// Set new timer to reset tap count after 5 seconds
		const timer = setTimeout(() => {
			tapCountRef.current = 0;
			tapTimerRef.current = null;
		}, 5000);
		tapTimerRef.current = timer;

		// If 3 taps detected, set mock data
		if (tapCountRef.current >= 3) {
			setMagtekData({
				firstName: 'John',
				lastName: 'Doe',
				cardNumber: '4111111111111111',
				maskedCardNumber: '4111********1111',
				unmaskedCardNumber: '4111111111111111',
				lastFourDigits: '1111',
				cardType: 'Visa',
				expirationDate: '12/25',
				expirationMonth: '12',
				expirationYear: '25',
				invoiceCloudDataString: 'K|TESTDEVICE123|ABCD1234567890|1A2B3C4D5E6F|12345678||9876543210ABCDEF|',
			} as MagtekCardReaderCardData);
			tapCountRef.current = 0;
			clearTimeout(timer);
			tapTimerRef.current = null;
		}
	};

	// Cleanup timer on unmount
	useEffect(() => {
		return () => {
			if (tapTimerRef.current) {
				clearTimeout(tapTimerRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (cartItems.length > 1) {
			logger.logPP.error({}, 'Paying for more than one account at a time is not supported', new Error('Multiple accounts in cart'));
			throw new Error('Paying for more than one account at a time is not supported');
		}
	}, [cartItems, cartItems.length]);

	/**
	 * Handles the payment processing using MagTek card reader data.
	 * Processes the card payment, sends data to the API, and handles success/error states.
	 *
	 * @param {MagtekCardReaderCardData} data - Card reader data from MagTek device
	 * @returns {Promise<void>}
	 */
	const handlePayment = useCallback(
		async (data: MagtekCardReaderCardData) => {
			let response: PayWithMagTekDataResponse;
			const item = cartItems[0];
			if (!item) return;
			try {
				setCurrentState(ViewState.PROCESSING);
				setHasProcessed(true);
				const request: PayWithMagTekDataRequest = {
					utilityAccountNumber: item!.item.itemNumber,
					amountToPay: item!.amountToPayWithFee,
					amountDue: item!.amountToPay,
					cardData: data,
					customerName: item!.customer!.name!,
				};

				const legacyPostAcceptedPayment: LegacyPostAcceptedPayment = structuredClone({
					dynatouchGUID: legacyId!,
					firstCashTransaction: false,
					customerID: companyId.toString(),
					kioskID: kioskId.toString(),
					paymentGatewayExternalID: item.item.itemNumber,
					paymentDateTime: new Date().toISOString(),
					paymentAmount: item!.amountToPayWithFee!,
					paymentType: 'Card',
					paymentGUID: guid(),
					passKey: await passKeyHelper.generatePassKey(),
					cashOnes: 0,
					cashTwos: 0,
					cashFives: 0,
					cashTens: 0,
					cashTwenties: 0,
					cashFifties: 0,
					cashHundreds: 0,
					cisProvider: enumHelper.getCisConfigTypeLabel(cisType),
					paymentGateway: enumHelper.getPpConfigTypeLabel(ppType),
					cisProviderExternalID: item!.item.itemNumber,
					payorName: item!.customer!.name!,
					paymentReference: paymentReferenceId,
				});

				const legacyPostRejectedPayment: LegacyPostRejectedPayment = structuredClone({
					customerID: companyId.toString(),
					kioskID: kioskId.toString(),
					billpaySessionGUID: legacyId!,
					rejectedECID: '',
					rejectedReason: 'Card payment error',
					rejectedPaymentType: 'Card',
					paymentAmount: item!.amountToPayWithFee!,
					payorName: item!.customer!.name!,
					paymentDateTime: new Date().toISOString(),
					passKey: await passKeyHelper.generatePassKey(),
				});

				response = await api.payments.magtekData(request, legacyPostAcceptedPayment, legacyPostRejectedPayment);
				const responseData = {
					customerName: item!.customer!.name!,
					amountToPay: item!.amountToPay!,
					paymentId: response!.paymentId,
					cISConfig: response!.cisConfigType,
					pPConfig: response!.ppConfigType,
					referenceNumberValue: response!.referenceNumberValue,
					referenceNumberLabel: response!.referenceNumberLabel,
				};
				await logger.logCardPayment.log(
					{
						...responseData,
						referenceNumberLabel: response!.referenceNumberLabel,
						cardAmount: item!.amountPaid!,
					},
					'Payment successful'
				);
				handleNavigation('payment-complete');
			} catch (error) {
				setError({ message: getString('card-read-error') });
				setErrorHint(getString('card-read-hint'));
				setCurrentState(ViewState.ERROR);
				await logger.logCardPayment.error(
					{
						amountToPay: item!.amountToPayWithFee!,
						cardAmount: item!.amountToPay!,
						paymentId: response!.paymentId ?? '',
						customerName: item!.customer!.name!,
						cISConfig: cisType,
						pPConfig: ppType,
						referenceNumberLabel: response!.referenceNumberLabel ?? '',
						referenceNumberValue: response!.referenceNumberValue ?? '',
					},
					'Payment failed:',
					error
				);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[cartItems, handleNavigation, legacyId, paymentReferenceId]
	);

	useEffect(() => {
		if (magtekData && currentState === ViewState.PAY && !hasProcessed) handlePayment(magtekData);
	}, [magtekData, handlePayment, currentState, hasProcessed]);

	/**
	 * Handles retry action after a payment error.
	 * Resets error state and returns to the payment view.
	 */
	const handleRetry = () => {
		setError(null);
		setCurrentState(ViewState.PAY);
		setHasProcessed(false);
	};

	/**
	 * Handles cancel action after a payment error.
	 * Resets error state and returns to the payment view.
	 * TODO: Navigate away or emit cancel event
	 */
	const handleCancel = () => {
		setError(null);
		setCurrentState(ViewState.PAY);
		setHasProcessed(false);
		// TODO: Navigate away or emit cancel event
	};

	/**
	 * Renders the payment view with card image, payment details, and instructions.
	 * @returns {JSX.Element} The payment view
	 */
	const renderPayView = () => {
		const item = cartItems[0];
		return (
			<section className={style.section}>
				<img className={style.section__image} src={cardEmvImage} alt={imageAltKey} onClick={handleImageClick} style={{ cursor: 'pointer' }} />
				<BalancesKvpList
					className={style.section__details}
					kvpList={[
						{
							label: getString(feeLabelKey ?? ''),
							value: item?.feeAmount ?? 0,
						},
						{ label: getString(paymentAmountLabelKey ?? ''), value: item!.amountToPayWithFee! },
					]}
					totalSum={{ label: getString(paymentTotalLabelKey ?? ''), value: item!.amountToPayWithFee! }}
				/>
				<MessageBoxSet
					className={style.section__messages}
					infoText={getString(infoBoxLabelKey ?? '')}
					warningText={getString(warningBoxLabelKey ?? '')}
				/>
			</section>
		);
	};

	/**
	 * Renders the processing view with a loading spinner.
	 * @returns {JSX.Element} The processing view
	 */
	const renderProcessingView = () => (
		<section className={style.processing}>
			<div className={style.processing__spinner}></div>
			<h2 className={style.processing__title}>{getString(processingPaymentLabelKey)}</h2>
		</section>
	);

	/**
	 * Renders the error view with error message and retry/cancel options.
	 * @returns {JSX.Element} The error view
	 */
	const renderErrorView = () => (
		<section className={style.error}>
			<MainMessageBox icon={AppIconType.Error} message={error?.message ?? getString(cardErrorGenericLabelKey)} />
			<Confirm
				titleLabel={getString(tryAgainMessageLabelKey)}
				yesButton={{ label: tryAgainYesLabelKey ?? getString('yes'), value: 'yes', target: '' }}
				noButton={{ label: tryAgainNoLabelKey ?? getString('no'), value: 'no', target: '' }}
				yesCallback={handleRetry}
				noCallback={handleCancel}
			/>
			<MessageBox type='warning' dangerouslySetInnerHTML={{ __html: errorHint ? errorHint : getString('card-read-hint') }} />
		</section>
	);

	/**
	 * Renders the appropriate view based on current state.
	 * @returns {JSX.Element} The current view based on ViewState
	 */
	const renderCurrentView = () => {
		switch (currentState) {
			case ViewState.PAY:
				return renderPayView();
			case ViewState.PROCESSING:
				return renderProcessingView();
			case ViewState.ERROR:
				return renderErrorView();
			default:
				return renderPayView();
		}
	};

	return <section className={style.section}>{renderCurrentView()}</section>;
}

export default PaymentCardEmvMock;
